import { useMap } from "@vis.gl/react-maplibre";
import { MapMouseEvent } from "maplibre-gl";
import { MutableRefObject, useEffect } from "react";

import { getAliveMap } from "@ryde/controls";

/**
 * Map wiring for draw mode: every canvas click appends a point, the cursor becomes a crosshair, and the
 * container is marked so drawing-mode styling applies. Clicks that land on the floating toolbar are ignored —
 * it sits over the canvas, so a button press would otherwise also drop a vertex under it.
 */
export const useMapLibrePolylineDrawClicks = ({
  isDrawing,
  toolbarRef,
  onAddPoint,
}: {
  isDrawing: boolean;
  toolbarRef: MutableRefObject<HTMLDivElement | null>;
  onAddPoint: (lat: number, lng: number) => void;
}): void => {
  const { current: mapRef } = useMap();

  useEffect(() => {
    const map = getAliveMap(mapRef);
    if (!map || !isDrawing) {
      return;
    }

    const canvas = map.getCanvas();
    const parent = map.getContainer()?.parentElement;

    const handleClick = (event: MapMouseEvent): void => {
      const target = event.originalEvent.target as Node | null;
      if (target && toolbarRef.current?.contains(target)) {
        return;
      }
      onAddPoint(event.lngLat.lat, event.lngLat.lng);
    };

    map.on("click", handleClick);
    canvas.style.cursor = "crosshair";
    parent?.classList.add("polyline-drawing-mode");

    return () => {
      parent?.classList.remove("polyline-drawing-mode");
      const aliveMap = getAliveMap(mapRef);
      if (!aliveMap) {
        return;
      }

      aliveMap.off("click", handleClick);
      canvas.style.cursor = "";
    };
  }, [mapRef, isDrawing, toolbarRef, onAddPoint]);
};
